import { ref } from 'vue';
import { useModal } from 'vue-final-modal';
import ConfirmCloseModal from '@/shared/ui/modals/ConfirmCloseModal.vue';
import { extractApiErrorMessage, useCustomToast } from '@/shared/lib';
import { trainingCollaboratorApi } from '@/entities/Training';
import {
  CERTIFICATE_OVERWRITE_DESCRIPTION,
  CERTIFICATE_OVERWRITE_TITLE,
} from '../lib/certificateOverwriteMessages';

export interface CertificateGenerationTarget {
  memberTrainingId: number;
  hasCertificate: boolean;
}

/**
 * O modal de sobrescrita é criado uma única vez, no setup de quem chama, pelo mesmo motivo do
 * fluxo de upload: criar um `useModal` por clique deixaria uma instância dinâmica órfã a cada
 * certificado gerado, porque a limpeza da vue-final-modal depende do unmount do dono.
 */
export function useCertificateGeneration(onGenerated: () => void) {
  const { showSuccessToast, showErrorToast } = useCustomToast();

  const generatingIds = ref(new Set<number>());

  let pendingTrainingId: number | null = null;
  let pendingMemberTrainingId: number | null = null;

  const overwriteModal = useModal({
    component: ConfirmCloseModal,
    attrs: {
      clickToClose: false,
      escToClose: false,
      title: CERTIFICATE_OVERWRITE_TITLE,
      description: CERTIFICATE_OVERWRITE_DESCRIPTION,
      onClose: () => {
        overwriteModal.close();
      },
      onConfirm: async () => {
        overwriteModal.close();
        await generate();
      },
    },
  });

  function isGenerating(memberTrainingId: number): boolean {
    return generatingIds.value.has(memberTrainingId);
  }

  async function generate(): Promise<void> {
    const trainingId = pendingTrainingId;
    const memberTrainingId = pendingMemberTrainingId;

    if (trainingId === null || memberTrainingId === null) return;
    if (generatingIds.value.has(memberTrainingId)) return;

    generatingIds.value.add(memberTrainingId);

    try {
      await trainingCollaboratorApi.generateCollaboratorCertificate(trainingId, memberTrainingId);

      showSuccessToast({ title: 'Certificado gerado com sucesso', type: 'success' });
      onGenerated();
    } catch (error) {
      showErrorToast({
        title: 'Erro ao gerar certificado. Tente novamente',
        message: extractApiErrorMessage(error),
        type: 'error',
      });
    } finally {
      generatingIds.value.delete(memberTrainingId);
    }
  }

  async function openCertificateGeneration(
    trainingId: number,
    target: CertificateGenerationTarget
  ): Promise<void> {
    pendingTrainingId = trainingId;
    pendingMemberTrainingId = target.memberTrainingId;

    if (target.hasCertificate) {
      overwriteModal.open();
      return;
    }

    await generate();
  }

  return { generatingIds, isGenerating, openCertificateGeneration };
}

export default useCertificateGeneration;
